import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  fetchProgramAuditSnapshotForProgramPage,
  type ProgramAuditPageSnapshotParams,
  type ProgramAuditSnapshot,
} from '../../services/programDataAuditService';
import { loadProgramPresentationAuditSnapshotCache } from '../../utils/presentationAuditSnapshotCache';
import { schoolKeys } from '../keys';
import { lsInitialDataOptions } from './lsInitialData';

export type UseProgramPresentationAuditQueryParams = ProgramAuditPageSnapshotParams & {
  scopeSignature: string;
  enabled?: boolean;
  forceRefresh?: boolean;
};

export function useProgramPresentationAuditQuery(
  params: UseProgramPresentationAuditQueryParams
): UseQueryResult<ProgramAuditSnapshot> {
  const {
    workspaceId,
    academicYear,
    termNumber,
    gradeId,
    scheduleSlotKeys,
    scopeType,
    scopeKey,
    includeFixedRows,
    scopeSignature,
    enabled = true,
    forceRefresh = false,
  } = params;

  const ls = loadProgramPresentationAuditSnapshotCache(
    workspaceId,
    academicYear,
    termNumber,
    gradeId,
    scopeSignature
  );
  const lsSnapshot: ProgramAuditSnapshot | undefined = ls
    ? {
        annualTimetableCells: [],
        scopedRows: [],
        fixedRows: [],
        programProgressCells: [],
        presentationEntries: ls.presentationEntries,
        worksheetEntries: [],
      }
    : undefined;

  return useQuery<ProgramAuditSnapshot>({
    queryKey: schoolKeys.programPresentationAudit(
      workspaceId,
      academicYear,
      termNumber,
      gradeId,
      scopeSignature
    ),
    enabled: enabled && !!workspaceId.trim() && !!gradeId.trim(),
    ...lsInitialDataOptions(!!ls, lsSnapshot!, ls?.savedAt),
    queryFn: () =>
      fetchProgramAuditSnapshotForProgramPage(
        {
          workspaceId,
          academicYear,
          termNumber,
          gradeId,
          scheduleSlotKeys,
          scopeType,
          scopeKey,
          includeFixedRows,
        },
        { forceRefresh, scopeSignature }
      ),
  });
}
